import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { FiDollarSign, FiPlus, FiTrendingDown, FiTrendingUp, FiTrash2 } from 'react-icons/fi';
import { AppLayout } from '../../components/AppLayout';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Input } from '../../components/Input';
import { Kpi } from '../../components/Kpi';
import { MoedaInput } from '../../components/MoedaInput';
import { Select } from '../../components/Select';
import {
  criarCategoriaFinanceiro,
  criarLancamento,
  listarCategoriasFinanceiro,
  listarLancamentos,
  removerLancamento,
} from '../../api/financeiro';
import { CategoriaFinanceira, LancamentoFinanceiro, ResumoFinanceiro } from '../../types';
import { formatarData, formatarMoeda } from '../../utils/formatadores';

interface FormLancamento {
  tipo: 'entrada' | 'saida';
  categoria_id: string;
  valor: number | string;
  descricao: string;
  data: string;
}

const FORM_INICIAL: FormLancamento = { tipo: 'saida', categoria_id: '', valor: '', descricao: '', data: '' };

export function FinanceiroLista() {
  const [categorias, setCategorias] = useState<CategoriaFinanceira[]>([]);
  const [lancamentos, setLancamentos] = useState<LancamentoFinanceiro[]>([]);
  const [resumo, setResumo] = useState<ResumoFinanceiro>({ total_entradas: 0, total_saidas: 0, saldo: 0 });
  const [carregando, setCarregando] = useState(true);
  const [form, setForm] = useState<FormLancamento>(FORM_INICIAL);
  const [salvando, setSalvando] = useState(false);

  function carregar() {
    setCarregando(true);
    Promise.all([listarCategoriasFinanceiro(), listarLancamentos()])
      .then(([categoriasResp, lancamentosResp]) => {
        setCategorias(categoriasResp);
        setLancamentos(lancamentosResp.lancamentos);
        setResumo(lancamentosResp.resumo);
      })
      .finally(() => setCarregando(false));
  }

  useEffect(carregar, []);

  const categoriasDoTipo = useMemo(
    () => categorias.filter((categoria) => categoria.tipo === form.tipo),
    [categorias, form.tipo]
  );

  const ultimosLancamentos = useMemo(() => lancamentos.slice(0, 8), [lancamentos]);

  function handleChange(campo: keyof FormLancamento) {
    return (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm((atual) => ({ ...atual, [campo]: event.target.value }));
  }

  function handleTipoChange(event: ChangeEvent<HTMLSelectElement>) {
    setForm((atual) => ({ ...atual, tipo: event.target.value as FormLancamento['tipo'], categoria_id: '' }));
  }

  async function handleNovaCategoria() {
    const { value: nome } = await Swal.fire({
      title: `Nova categoria de ${form.tipo === 'entrada' ? 'entrada' : 'saida'}`,
      input: 'text',
      inputLabel: 'Nome da categoria',
      showCancelButton: true,
      confirmButtonText: 'Criar',
      cancelButtonText: 'Cancelar',
    });

    if (!nome) return;

    const categoria = await criarCategoriaFinanceiro(nome, form.tipo);
    setCategorias((atual) => [...atual, categoria]);
    setForm((atual) => ({ ...atual, categoria_id: String(categoria.id) }));
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setSalvando(true);

    try {
      await criarLancamento({
        categoria_id: form.categoria_id,
        valor: form.valor,
        descricao: form.descricao,
        data: form.data || undefined,
      });
      setForm({ ...FORM_INICIAL, tipo: form.tipo });
      carregar();
      Swal.fire({ icon: 'success', title: 'Lancamento salvo!', timer: 1000, showConfirmButton: false });
    } catch (error: any) {
      Swal.fire({
        icon: 'error',
        title: 'Nao foi possivel salvar',
        text: error.response?.data?.erro || 'Verifique os dados informados.',
      });
    } finally {
      setSalvando(false);
    }
  }

  async function handleRemover(lancamento: LancamentoFinanceiro) {
    const confirmacao = await Swal.fire({
      icon: 'warning',
      title: 'Remover este lancamento?',
      showCancelButton: true,
      confirmButtonText: 'Remover',
      cancelButtonText: 'Cancelar',
    });

    if (!confirmacao.isConfirmed) return;

    await removerLancamento(lancamento.id);
    carregar();
  }

  return (
    <AppLayout>
      <div className="lizzi-cabecalho-pagina">
        <h1>
          <span className="lizzi-icone-titulo">
            <FiDollarSign size={18} />
          </span>
          Financeiro
        </h1>
      </div>

      <div className="lizzi-kpi-grid" style={{ marginBottom: '20px' }}>
        <Kpi titulo="Entradas" valor={formatarMoeda(resumo.total_entradas)} tone="sucesso" icone={<FiTrendingUp size={20} />} />
        <Kpi titulo="Saidas" valor={formatarMoeda(resumo.total_saidas)} tone="erro" icone={<FiTrendingDown size={20} />} />
        <Kpi titulo="Saldo" valor={formatarMoeda(resumo.saldo)} tone={resumo.saldo >= 0 ? 'sucesso' : 'erro'} icone={<FiDollarSign size={20} />} />
      </div>

      <div className="lizzi-grid-2">
        <Card className="lizzi-section-card">
          <div className="lizzi-section-header">
            <div>
              <h2>Novo lancamento</h2>
              <p>Registre entradas e saidas com mais clareza.</p>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="lizzi-form-grid-2">
              <Select
                label="Tipo"
                value={form.tipo}
                onChange={handleTipoChange}
                options={[
                  { value: 'saida', label: 'Saida' },
                  { value: 'entrada', label: 'Entrada' },
                ]}
              />

              <div className="lizzi-inline-field-group">
                <Select
                  label="Categoria"
                  value={form.categoria_id}
                  onChange={handleChange('categoria_id')}
                  options={categoriasDoTipo.map((categoria) => ({ value: categoria.id, label: categoria.nome }))}
                  placeholder="Selecione"
                  required
                />
                <div className="lizzi-inline-field-button">
                  <Button type="button" variant="outline" onClick={handleNovaCategoria} fullWidth={false}>
                    <FiPlus size={15} style={{ marginRight: '6px' }} />
                    Nova
                  </Button>
                </div>
              </div>

              <MoedaInput
                label="Valor"
                value={form.valor}
                onValueChange={(valor) => setForm((atual) => ({ ...atual, valor }))}
                required
              />

              <Input label="Data" type="date" value={form.data} onChange={handleChange('data')} />

              <div className="lizzi-grid-span-2">
                <Input label="Descricao" value={form.descricao} onChange={handleChange('descricao')} placeholder="Ex.: aluguel, venda, reposicao" />
              </div>
            </div>

            <div className="lizzi-modal-actions" style={{ justifyContent: 'flex-start' }}>
              <Button type="submit" disabled={salvando} fullWidth={false}>
                {salvando ? 'Salvando...' : 'Lancar'}
              </Button>
            </div>
          </form>
        </Card>

        <Card className="lizzi-section-card">
          <div className="lizzi-section-header">
            <div>
              <h2>Ultimos lancamentos</h2>
              <p>Visao rapida do que entrou e saiu.</p>
            </div>
          </div>

          {carregando ? (
            <p>Carregando...</p>
          ) : ultimosLancamentos.length === 0 ? (
            <div className="lizzi-empty-state">
              <span>Nenhum lancamento registrado ainda.</span>
            </div>
          ) : (
            <div className="lizzi-finance-list">
              {ultimosLancamentos.map((lancamento) => (
                <div key={lancamento.id} className="lizzi-finance-item">
                  <div>
                    <strong>{lancamento.categoria_nome}</strong>
                    <p>{lancamento.descricao || 'Sem descricao'}</p>
                    <small>{formatarData(lancamento.data)}</small>
                  </div>
                  <div className="lizzi-finance-item-side">
                    <b className={lancamento.tipo === 'entrada' ? 'sucesso' : 'erro'}>
                      {lancamento.tipo === 'entrada' ? '+' : '-'} {formatarMoeda(lancamento.valor)}
                    </b>
                    <button type="button" className="lizzi-icon-button" onClick={() => handleRemover(lancamento)} aria-label="Remover lancamento">
                      <FiTrash2 size={15} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </AppLayout>
  );
}
